import { CSSProperties, FC } from 'react';
import { useLocation } from 'react-router';
import { useWindowSizes } from '../../scripts/hooks/useWindowSizes';
import { useFeatures, useSiteConfig } from '../../scripts/selectors/global';
import { TDisplayAdsProvider, ZoneContainer, TZoneType } from './zone';
import { STICKY_EDGE_AD_MIN_VIEWPORT_WIDTH } from './adViewportThresholds';
import { doesPathMatchStickyEdgeAdPrefixes, STICKY_EDGE_AD_DISPLAY_ADS_PROVIDERS } from './stickyEdgeAdsShared';

const EDGE_AD_WIDTH = 300;
const SITE_CONTENT_MAX_WIDTH = '90rem';
const EDGE_AD_TOP_PADDING = 12;

type TStickyEdgeAdsProps = {
  allowedPathPrefixes?: string[];
  deniedPathPrefixes?: string[];
  allowedDisplayAdsProviders?: TDisplayAdsProvider[];
  leftZoneType?: TZoneType;
  minViewportWidth?: number;
  rightZoneType?: TZoneType;
  showLeft?: boolean;
  showRight?: boolean;
  sideOffset?: number;
  topOffset?: number;
  zIndex?: number;
};

const isAllowedDisplayAdsProvider = (
  provider: string | undefined,
  allowedProviders: readonly TDisplayAdsProvider[]
): provider is TDisplayAdsProvider =>
  !!provider && allowedProviders.some((allowedProvider) => allowedProvider === provider);

const buildRailStyle = (
  side: 'left' | 'right',
  sideOffset: number,
  topOffset: number,
  zIndex: number
): CSSProperties => ({
  position: 'fixed',
  top: topOffset,
  [side]: `max(${sideOffset}px, calc((100vw - ${SITE_CONTENT_MAX_WIDTH} - ${EDGE_AD_WIDTH * 2}px) / 4))`,
  width: EDGE_AD_WIDTH,
  paddingTop: EDGE_AD_TOP_PADDING,
  zIndex,
  pointerEvents: 'none'
});

const EDGE_AD_INNER_STYLE: CSSProperties = {
  width: EDGE_AD_WIDTH,
  pointerEvents: 'auto'
};

export const StickyEdgeAds: FC<TStickyEdgeAdsProps> = ({
  allowedPathPrefixes = [],
  deniedPathPrefixes = [],
  allowedDisplayAdsProviders = STICKY_EDGE_AD_DISPLAY_ADS_PROVIDERS,
  leftZoneType = 'left_edge_ad',
  minViewportWidth = STICKY_EDGE_AD_MIN_VIEWPORT_WIDTH,
  rightZoneType = 'right_edge_ad',
  showLeft = true,
  showRight = true,
  sideOffset = 10,
  topOffset = 84,
  zIndex = 2
}) => {
  const currentFeatures = useFeatures();
  const siteConfig = useSiteConfig();
  const location = useLocation();
  const { width } = useWindowSizes();
  const { displayAdsProvider } = siteConfig;

  if (currentFeatures.no_ads || (!showLeft && !showRight)) {
    return null;
  }

  if (!isAllowedDisplayAdsProvider(displayAdsProvider, allowedDisplayAdsProviders)) {
    return null;
  }

  if (allowedPathPrefixes.length > 0 && !doesPathMatchStickyEdgeAdPrefixes(location.pathname, allowedPathPrefixes)) {
    return null;
  }

  if (deniedPathPrefixes.length > 0 && doesPathMatchStickyEdgeAdPrefixes(location.pathname, deniedPathPrefixes)) {
    return null;
  }

  // 90rem main content + two 300px rails + gutters fits safely at 2080px and above.
  if (width < minViewportWidth) {
    return null;
  }

  return (
    <>
      {showLeft && (
        <div
          className="rio-edge-ad rio-edge-ad--left"
          data-rio-edge-ad="left"
          style={buildRailStyle('left', sideOffset, topOffset, zIndex)}
        >
          <div style={EDGE_AD_INNER_STYLE}>
            <ZoneContainer type={leftZoneType} destroyOnUnmount={true} />
          </div>
        </div>
      )}

      {showRight && (
        <div
          className="rio-edge-ad rio-edge-ad--right"
          data-rio-edge-ad="right"
          style={buildRailStyle('right', sideOffset, topOffset, zIndex)}
        >
          <div style={EDGE_AD_INNER_STYLE}>
            <ZoneContainer type={rightZoneType} destroyOnUnmount={true} />
          </div>
        </div>
      )}
    </>
  );
};
